import { Link } from 'react-router-dom';
import { Stack, Text, Group, Rating, Skeleton, Anchor, Divider, Avatar, Paper, Image } from '@mantine/core';
import { IconUser, IconExternalLink, IconChevronRight } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { reviewsService } from '../../services/reviews';
import { AdCard } from './AdCard';
import type { Review } from '../../types';

interface Props {
  reviewId: string;
}

export function ReviewDetailSidebar({ reviewId }: Props) {
  const { data: review } = useQuery({
    queryKey: ['review', reviewId],
    queryFn: () => reviewsService.getById(reviewId),
    staleTime: 5 * 60 * 1000,
    enabled: !!reviewId,
  });

  const authorId = review?.user?.id;
  const authorUsername = review?.user?.username;
  const authorImageUrl = review?.user?.profileImageUrl;
  const productAffiliateUrl = review?.product?.amazonAffiliateUrl;

  const { data: authorReviews, isLoading: loadingAuthor } = useQuery({
    queryKey: ['author-reviews', authorId, reviewId],
    queryFn: () => reviewsService.getByUserIdPaginated(authorId!, 1, 4, reviewId),
    staleTime: 5 * 60 * 1000,
    enabled: !!authorId,
  });

  const { data: similarReviews, isLoading: loadingSimilar } = useQuery({
    queryKey: ['similar-reviews', reviewId],
    queryFn: () => reviewsService.getSimilar(reviewId, 4),
    staleTime: 2 * 60 * 60 * 1000,
    enabled: !!reviewId,
  });

  const authorItems = authorReviews?.items ?? [];
  const similarItems = similarReviews ?? [];

  return (
    <Stack gap="lg">
      {/* Author's other reviews */}
      {(loadingAuthor || authorItems.length > 0) && (
        <Stack gap="xs">
          <Group gap="xs">
            <Avatar src={authorImageUrl} size="sm" radius="xl"><IconUser size={14} /></Avatar>
            <Text size="sm" fw={600}>
              More from{' '}
              {authorUsername && (
                <Anchor component={Link} to={`/users/${authorUsername}`} size="sm" fw={600}>
                  @{authorUsername}
                </Anchor>
              )}
            </Text>
          </Group>
          <Stack gap="xs">
            {loadingAuthor ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Group key={i} gap="xs" wrap="nowrap">
                  <Skeleton h={48} w={48} radius="sm" style={{ flexShrink: 0 }} />
                  <Stack gap={4} style={{ flex: 1 }}>
                    <Skeleton h={12} w="90%" />
                    <Skeleton h={10} w="40%" />
                  </Stack>
                </Group>
              ))
            ) : (
              authorItems.map((r: Review) => (
                <ReviewMiniCard key={r.id} review={r} showProduct />
              ))
            )}
          </Stack>
        </Stack>
      )}

      {/* Similar reviews */}
      {(loadingSimilar || similarItems.length > 0) && (
        <>
          <Divider />
          <Stack gap="xs">
            <Text size="sm" fw={600}>More Reviews You Might Like</Text>
            <Stack gap="xs">
              {loadingSimilar ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <Group key={i} gap="xs" wrap="nowrap">
                    <Skeleton h={48} w={48} radius="sm" style={{ flexShrink: 0 }} />
                    <Stack gap={4} style={{ flex: 1 }}>
                      <Skeleton h={12} w="90%" />
                      <Skeleton h={10} w="60%" />
                    </Stack>
                  </Group>
                ))
              ) : (
                similarItems.map((r: Review) => (
                  <ReviewMiniCard key={r.id} review={r} showProduct />
                ))
              )}
            </Stack>
          </Stack>
        </>
      )}

      {/* Affiliate ad or fallback */}
      <Divider />
      {productAffiliateUrl ? (
        <Anchor
          href={productAffiliateUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          size="sm"
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
        >
          View on Amazon (paid link) <IconExternalLink size={12} />
        </Anchor>
      ) : (
        <AdCard />
      )}

      <Divider />
      <Stack gap={4}>
        <Text size="xs" c="dimmed">
          <Anchor component={Link} to="/privacy" size="xs" c="dimmed">Privacy</Anchor>
          {' · '}
          <Anchor component={Link} to="/terms" size="xs" c="dimmed">Terms</Anchor>
        </Text>
        <Text size="xs" c="dimmed">© {new Date().getFullYear()} CartCritic</Text>
      </Stack>
    </Stack>
  );
}

function ReviewMiniCard({ review, showProduct }: { review: Review; showProduct?: boolean }) {
  return (
    <Paper
      component={Link}
      to={`/reviews/${review.id}`}
      withBorder
      radius="sm"
      p="xs"
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color 120ms, background-color 120ms',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--mantine-color-brand-5)';
        e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '';
        e.currentTarget.style.backgroundColor = '';
      }}
    >
      <Group gap="xs" wrap="nowrap" align="flex-start">
        {review.imageUrls?.[0] && (
          <Image
            src={review.imageUrls[0]}
            w={48}
            h={48}
            radius="sm"
            fit="cover"
            style={{ flexShrink: 0, border: '1px solid var(--mantine-color-default-border)' }}
            alt=""
          />
        )}
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          {showProduct && review.product && (
            <Text size="xs" fw={600} lineClamp={1}>{review.product.name}</Text>
          )}
          <Rating value={review.rating} readOnly size="xs" />
          <Text size="xs" c="dimmed" lineClamp={2}>{review.content}</Text>
        </Stack>
        <IconChevronRight size={14} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0, marginTop: 2 }} />
      </Group>
    </Paper>
  );
}
